/**
 * Textarea Character Limit Notice
 *
 * Automatically adds character count and warning notice to any textarea with maxlength attribute
 *
 * Usage:
 * 1. Add this script to your theme
 * 2. Add maxlength attribute to your textarea
 * 3. Optionally add data-warning-threshold="80" to customize when warning appears (default: 80%)
 *
 * Example:
 * <textarea maxlength="200" data-warning-threshold="75"></textarea>
 */

class TextareaLimit {
  constructor(textarea) {
    this.textarea = textarea;
    this.maxLength = parseInt(textarea.getAttribute('maxlength'));
    this.warningThreshold = parseInt(textarea.dataset.warningThreshold || 80);
    this.warningAt = this.warningThreshold

    if (!this.maxLength) return;

    this.createElements();
    this.init();
  }

  createElements() {
    // Create wrapper if textarea isn't already wrapped
    if (!this.textarea.parentElement.classList.contains('textarea-limit-wrapper')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'textarea-limit-wrapper';
      this.textarea.parentNode.insertBefore(wrapper, this.textarea);
      wrapper.appendChild(this.textarea);
    }

    // Create info container (counter + notice on same line)
    this.infoContainer = document.createElement('div');
    this.infoContainer.className = 'textarea-limit-info';
    this.infoContainer.style.display = 'none';
    this.infoContainer.style.fontSize = '1rem';
    this.infoContainer.style.minHeight = '1.5em';
    this.infoContainer.style.marginTop = '0.25rem';
    this.infoContainer.innerHTML = `
      <span class="textarea-limit-counter">
        <span class="current">0</span> / <span class="max">${this.maxLength}</span>
      </span>
      <span class="textarea-limit-notice"></span>
    `;
    this.textarea.parentElement.appendChild(this.infoContainer);

    this.currentCount = this.infoContainer.querySelector('.current');
    this.notice = this.infoContainer.querySelector('.textarea-limit-notice');
  }

  init() {
    this.updateCount();
    this.textarea.addEventListener('input', () => this.updateCount());
  }

  updateCount() {
    const length = this.textarea.value.length;
    this.currentCount.textContent = length;

    // Reset classes
    this.textarea.classList.remove('near-limit', 'at-limit');
    this.infoContainer.classList.remove('near-limit', 'at-limit');

    if (length >= this.maxLength) {
      // At limit
      this.textarea.classList.add('at-limit');
      this.infoContainer.classList.add('at-limit');
      this.notice.textContent = ' - Character limit reached';
      this.infoContainer.style.display = 'block';
    } else if (length >= this.warningAt) {
      // Near limit
      this.textarea.classList.add('near-limit');
      this.infoContainer.classList.add('near-limit');
      this.notice.textContent = " - You're approaching the character limit";
      this.infoContainer.style.display = 'block';
    } else {
      // Normal - hide everything
      this.infoContainer.style.display = 'none';
    }
  }
}

// Auto-initialize all textareas with maxlength
function initTextareaLimits() {
  document.querySelectorAll('textarea[maxlength]').forEach(textarea => {
    if (!textarea.dataset.textareaLimitInitialized) {
      new TextareaLimit(textarea);
      textarea.dataset.textareaLimitInitialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTextareaLimits);
} else {
  initTextareaLimits();
}

// Watch for dynamically added textareas
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(() => initTextareaLimits());
  observer.observe(document.body, { childList: true, subtree: true });
}
